import React, { useEffect, useState } from 'react';
import {
  Card, Input, Button, Select, Typography, Space, message, Spin, Switch, Divider, ColorPicker, Row, Col,
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, FormOutlined, PlusOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const FIELD_LABEL = {
  email: 'Email address', first_name: 'First name', last_name: 'Last name',
  phone: 'Phone', city: 'City', state: 'State', country: 'Country',
};

const DEFAULT_FIELDS = [
  { key: 'email', enabled: true, required: true },
  { key: 'first_name', enabled: true, required: false },
  { key: 'last_name', enabled: false, required: false },
  { key: 'phone', enabled: false, required: false },
  { key: 'city', enabled: false, required: false },
  { key: 'state', enabled: false, required: false },
  { key: 'country', enabled: false, required: false },
];

export default function CreateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [lists, setLists]     = useState([]);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);

  const [f, setF] = useState({
    name: '', list_id: undefined, status: 'active',
    fields: DEFAULT_FIELDS,
    double_opt_in: false, hcaptcha_enabled: false,
    heading: 'Join our list', description: '',
    button_text: 'Subscribe', success_message: 'Thanks for subscribing!',
    brand_color: '#0a40d9', redirect_url: '',
  });
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    (async () => {
      try {
        const listRes = await axios.get('/api/lists');
        setLists(Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data || []));
        if (isEdit) {
          const res = await axios.get(`/api/forms/${id}`);
          const d = res.data;
          set({
            name: d.name, list_id: d.list_id, status: d.status,
            fields: d.fields || DEFAULT_FIELDS,
            double_opt_in: d.double_opt_in, hcaptcha_enabled: d.hcaptcha_enabled,
            heading: d.heading || '', description: d.description || '',
            button_text: d.button_text || 'Subscribe', success_message: d.success_message || '',
            brand_color: d.brand_color || '#0a40d9', redirect_url: d.redirect_url || '',
          });
        }
      } catch { message.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const toggleField = (key, prop) => set({
    fields: f.fields.map(x => x.key === key ? { ...x, [prop]: !x[prop] } : x),
  });

  // Create a new list inline and select it
  const createList = async () => {
    const name = newListName.trim();
    if (!name) return;
    setCreatingList(true);
    try {
      const res = await axios.post('/api/lists', { name });
      const created = res.data;
      setLists(prev => [{ id: created.id, name: created.name }, ...prev]);
      set({ list_id: created.id });
      setNewListName('');
      message.success(`List "${created.name}" created`);
    } catch (e) {
      message.error(e.response?.data?.message || 'Could not create list');
    } finally {
      setCreatingList(false);
    }
  };

  const save = async () => {
    if (!f.name.trim()) { message.warning('Give your form a name'); return; }
    if (!f.list_id) { message.warning('Choose a target list'); return; }
    setSaving(true);
    try {
      let formId = id;
      if (!isEdit) {
        const res = await axios.post('/api/forms', { name: f.name.trim(), list_id: f.list_id });
        formId = res.data.id;
      }
      await axios.put(`/api/forms/${formId}`, {
        name: f.name.trim(), list_id: f.list_id, status: f.status,
        fields: f.fields, double_opt_in: f.double_opt_in, hcaptcha_enabled: f.hcaptcha_enabled,
        heading: f.heading, description: f.description, button_text: f.button_text,
        success_message: f.success_message, brand_color: f.brand_color, redirect_url: f.redirect_url || null,
      });
      message.success('Form saved');
      navigate('/forms');
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  const color = typeof f.brand_color === 'string' ? f.brand_color : f.brand_color?.toHexString?.() || '#0a40d9';

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/forms')}>Back</Button>
        <Title level={4} style={{ margin: 0 }}><FormOutlined /> {isEdit ? 'Edit' : 'New'} Signup Form</Title>
      </Space>

      <Row gutter={20}>
        {/* Config */}
        <Col xs={24} lg={14}>
          <Card title="Basics" style={{ borderRadius: 14, marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>Form name (internal)</Text>
            <Input value={f.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. Newsletter signup" style={{ margin: '4px 0 14px' }} />
            <Text type="secondary" style={{ fontSize: 13 }}>Add subscribers to list</Text>
            <Select
              style={{ width: '100%', marginTop: 4 }} placeholder="Select a list" value={f.list_id}
              onChange={v => set({ list_id: v })} options={lists.map(l => ({ value: l.id, label: l.name }))}
              showSearch optionFilterProp="label"
              popupRender={menu => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ display: 'flex', gap: 8, padding: '0 8px 4px' }}>
                    <Input
                      placeholder="New list name"
                      value={newListName}
                      onChange={e => setNewListName(e.target.value)}
                      onPressEnter={createList}
                      onKeyDown={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                    />
                    <Button type="text" icon={<PlusOutlined />} loading={creatingList} onMouseDown={e => e.preventDefault()} onClick={createList}>
                      Create
                    </Button>
                  </div>
                </>
              )}
            />
          </Card>

          <Card title="Fields" style={{ borderRadius: 14, marginBottom: 16 }}>
            {f.fields.map(field => (
              <div key={field.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <Text strong={field.key === 'email'}>{FIELD_LABEL[field.key]}</Text>
                <Space size={20}>
                  <Space size={6}><Text type="secondary" style={{ fontSize: 12 }}>Show</Text>
                    <Switch size="small" checked={field.enabled} disabled={field.key === 'email'} onChange={() => toggleField(field.key, 'enabled')} /></Space>
                  <Space size={6}><Text type="secondary" style={{ fontSize: 12 }}>Required</Text>
                    <Switch size="small" checked={field.required} disabled={field.key === 'email' || !field.enabled} onChange={() => toggleField(field.key, 'required')} /></Space>
                </Space>
              </div>
            ))}
          </Card>

          <Card title="Content & branding" style={{ borderRadius: 14, marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>Heading</Text>
            <Input value={f.heading} onChange={e => set({ heading: e.target.value })} style={{ margin: '4px 0 12px' }} />
            <Text type="secondary" style={{ fontSize: 13 }}>Description</Text>
            <Input.TextArea value={f.description} onChange={e => set({ description: e.target.value })} rows={2} style={{ margin: '4px 0 12px' }} />
            <Row gutter={12}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 13 }}>Button text</Text>
                <Input value={f.button_text} onChange={e => set({ button_text: e.target.value })} style={{ marginTop: 4 }} />
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 13 }}>Brand color</Text>
                <div style={{ marginTop: 4 }}><ColorPicker value={color} onChange={(c) => set({ brand_color: c.toHexString() })} showText /></div>
              </Col>
            </Row>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Success message</Text>
              <Input value={f.success_message} onChange={e => set({ success_message: e.target.value })} style={{ marginTop: 4 }} />
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Redirect URL after signup (optional)</Text>
              <Input value={f.redirect_url} onChange={e => set({ redirect_url: e.target.value })} placeholder="https://yoursite.com/thank-you" style={{ marginTop: 4 }} />
            </div>
          </Card>

          <Card title="Options" style={{ borderRadius: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div><Text strong>Double opt-in</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Email a confirmation link before adding (recommended for consent).</Text></div>
              <Switch checked={f.double_opt_in} onChange={v => set({ double_opt_in: v })} />
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div><Text strong>hCaptcha protection</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Adds a CAPTCHA to block bots.</Text></div>
              <Switch checked={f.hcaptcha_enabled} onChange={v => set({ hcaptcha_enabled: v })} />
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><Text strong>Active</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>Paused forms stop accepting submissions.</Text></div>
              <Switch checked={f.status === 'active'} onChange={v => set({ status: v ? 'active' : 'paused' })} />
            </div>
          </Card>

          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save form</Button>
        </Col>

        {/* Live preview */}
        <Col xs={24} lg={10}>
          <div style={{ position: 'sticky', top: 20 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>LIVE PREVIEW</Text>
            <div style={{ background: '#f5f5f7', borderRadius: 14, padding: 20 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                <h3 style={{ margin: '0 0 6px', color: '#111' }}>{f.heading || 'Heading'}</h3>
                {f.description && <p style={{ margin: '0 0 16px', color: '#666', fontSize: 13 }}>{f.description}</p>}
                {f.fields.filter(x => x.enabled).map(x => (
                  <div key={x.key} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, color: '#555', marginBottom: 3 }}>{FIELD_LABEL[x.key]}{x.required && <span style={{ color: '#ef4444' }}> *</span>}</div>
                    <div style={{ height: 38, border: '1px solid #d1d5db', borderRadius: 8, background: '#fafafa' }} />
                  </div>
                ))}
                <div style={{ height: 42, background: color, borderRadius: 8, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, marginTop: 6 }}>
                  {f.button_text || 'Subscribe'}
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
